// =============================================================================
// MeuDriver — useTripTracking (lado instrutor)
// -----------------------------------------------------------------------------
// Captura a posição do instrutor e a distribui por dois caminhos, com
// frequências diferentes e propósitos diferentes:
//
//   Broadcast (~5 s)  → o aluno vê o carro se mexendo. Efêmero, não toca o
//                       Postgres. É o que faz a experiência parecer Uber.
//   RPC ping (~30 s)  → amostragem gravada em trip_location_pings, só para
//                       auditoria de disputa. Expurgada em 7 dias.
//
// Usar postgres_changes para o tempo real custaria uma escrita no banco por
// posição, por instrutor ativo — carga e custo desnecessários para um dado que
// vale 5 segundos.
//
// Ver docs/mapa/modulos/rastreamento-ao-vivo.md
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import {
  watchPosition,
  getCurrentPosition,
  isBackgroundCapable,
  GeolocationError,
  type Position,
  type WatchHandle,
} from '@/lib/geolocation'
import type { TripStatus } from '@/types/database'

const BROADCAST_INTERVAL_MS = 5_000
const PING_INTERVAL_MS = 30_000

export interface TripBroadcastPayload {
  lat: number
  lng: number
  headingDeg: number | null
  accuracyM: number | null
  at: number
}

/** Nome do canal. O aluno assina exatamente o mesmo. */
export function tripChannelName(bookingId: string): string {
  return `trip:${bookingId}`
}

interface UseTripTrackingReturn {
  status: TripStatus | null
  isTracking: boolean
  lastPosition: Position | null
  error: string | null
  /** `false` no PWA: a posição para quando o app sai de foco. */
  backgroundCapable: boolean
  start: () => Promise<void>
  advance: (next: 'chegou' | 'embarcado' | 'encerrado' | 'cancelado') => Promise<void>
}

export function useTripTracking(bookingId: string | null): UseTripTrackingReturn {
  const [status, setStatus] = useState<TripStatus | null>(null)
  const [lastPosition, setLastPosition] = useState<Position | null>(null)
  const [error, setError] = useState<string | null>(null)

  const channelRef = useRef<RealtimeChannel | null>(null)
  const watchRef = useRef<WatchHandle | null>(null)
  const positionRef = useRef<Position | null>(null)
  const broadcastTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  const isTracking = status === 'a_caminho' || status === 'chegou'

  // ── Estado inicial ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!bookingId) return
    let mounted = true

    supabase
      .rpc('trip_get_live', { p_booking_id: bookingId })
      .then(({ data, error: err }) => {
        if (!mounted) return
        if (err) {
          Sentry.captureException(err, { tags: { source: 'useTripTracking.getLive' } })
          return
        }
        const payload = data as { status?: TripStatus } | null
        setStatus(payload?.status ?? 'nao_iniciado')
      })

    return () => {
      mounted = false
    }
  }, [bookingId])

  // ── Encerra tudo ───────────────────────────────────────────────────────────

  const teardown = useCallback(() => {
    watchRef.current?.stop()
    watchRef.current = null
    if (broadcastTimer.current) clearInterval(broadcastTimer.current)
    if (pingTimer.current) clearInterval(pingTimer.current)
    broadcastTimer.current = null
    pingTimer.current = null
    if (channelRef.current) {
      void supabase.removeChannel(channelRef.current)
      channelRef.current = null
    }
  }, [])

  useEffect(() => teardown, [teardown])

  // ── Liga a captura quando a corrida está ativa ─────────────────────────────

  useEffect(() => {
    if (!bookingId || !isTracking) {
      teardown()
      return
    }

    let cancelled = false

    async function begin() {
      const channel = supabase.channel(tripChannelName(bookingId!), {
        config: { broadcast: { self: false } },
      })
      await channel.subscribe()
      if (cancelled) {
        void supabase.removeChannel(channel)
        return
      }
      channelRef.current = channel

      try {
        watchRef.current = await watchPosition(
          (pos) => {
            positionRef.current = pos
            setLastPosition(pos)
            setError(null)
          },
          (err) => {
            // Falha de leitura isolada não derruba o rastreio; só a negativa
            // de permissão é terminal.
            if (err.code === 'permission_denied') {
              setError('Permissão de localização negada. O aluno não consegue ver você.')
              teardown()
            }
          },
        )
      } catch (err) {
        if (cancelled) return
        const msg =
          err instanceof GeolocationError
            ? err.message
            : 'Não foi possível acessar a localização.'
        setError(msg)
        return
      }

      broadcastTimer.current = setInterval(() => {
        const pos = positionRef.current
        if (!pos || !channelRef.current) return
        const payload: TripBroadcastPayload = {
          lat: pos.lat,
          lng: pos.lng,
          headingDeg: pos.headingDeg,
          accuracyM: pos.accuracyM,
          at: pos.timestamp,
        }
        void channelRef.current.send({ type: 'broadcast', event: 'position', payload })
      }, BROADCAST_INTERVAL_MS)

      pingTimer.current = setInterval(() => {
        const pos = positionRef.current
        if (!pos) return
        void supabase
          .rpc('trip_record_ping', {
            p_booking_id: bookingId!,
            p_lat: pos.lat,
            p_lng: pos.lng,
            p_accuracy_m: pos.accuracyM,
            p_heading_deg: pos.headingDeg,
            p_speed_ms: pos.speedMs,
          })
          .then(({ error: err }) => {
            // trip_not_active é esperado numa corrida recém-encerrada: o timer
            // pode disparar uma vez antes do teardown. Não vale telemetria.
            if (err && !err.message.includes('trip_not_active')) {
              Sentry.captureException(err, { tags: { source: 'useTripTracking.ping' } })
            }
          })
      }, PING_INTERVAL_MS)
    }

    void begin()

    return () => {
      cancelled = true
      teardown()
    }
  }, [bookingId, isTracking, teardown])

  // ── Ações ──────────────────────────────────────────────────────────────────

  const start = useCallback(async () => {
    if (!bookingId) return
    setError(null)

    // Carimba o marco com a posição de partida, mas não bloqueia por causa dela.
    let pos: Position | null = null
    try {
      pos = await getCurrentPosition()
    } catch {
      pos = null
    }

    const { error: err } = await supabase.rpc('trip_start', {
      p_booking_id: bookingId,
      p_lat: pos?.lat ?? null,
      p_lng: pos?.lng ?? null,
    })

    if (err) {
      setError(translateTripError(err.message))
      throw err
    }
    setStatus('a_caminho')
  }, [bookingId])

  const advance = useCallback(
    async (next: 'chegou' | 'embarcado' | 'encerrado' | 'cancelado') => {
      if (!bookingId) return
      const pos = positionRef.current
      const { error: err } = await supabase.rpc('trip_advance_status', {
        p_booking_id: bookingId,
        p_status: next,
        p_lat: pos?.lat ?? null,
        p_lng: pos?.lng ?? null,
      })
      if (err) {
        setError(translateTripError(err.message))
        throw err
      }
      setStatus(next)
    },
    [bookingId],
  )

  return {
    status,
    isTracking,
    lastPosition,
    error,
    backgroundCapable: isBackgroundCapable(),
    start,
    advance,
  }
}

/** Traduz os códigos levantados pelas RPCs para texto de tela. */
export function translateTripError(message: string): string {
  if (message.includes('location_consent_required'))
    return 'Você precisa autorizar o compartilhamento de localização antes de iniciar.'
  if (message.includes('booking_not_confirmed'))
    return 'Esta aula não está confirmada.'
  if (message.includes('too_early_to_start'))
    return 'Ainda é cedo para iniciar o deslocamento desta aula.'
  if (message.includes('tracking_window_closed'))
    return 'A janela de rastreamento desta aula já encerrou.'
  if (message.includes('not_booking_owner')) return 'Esta aula não é sua.'
  if (message.includes('invalid_transition')) return 'Esta etapa não pode ser feita agora.'
  if (message.includes('trip_not_active')) return 'O deslocamento não está ativo.'
  return 'Não foi possível concluir a ação. Tente novamente.'
}
